/**
 * Hero carousel — video or image slides.
 *
 * With one slide it does nothing: no timer, no controls, no listeners. The
 * hero is the LCP element on the homepage, so the single-slide case has to
 * stay exactly as cheap as it was before this file existed.
 *
 * Later slides carry their video sources as `data-src` and are loaded the
 * first time they are shown. Several autoplaying videos at once is the
 * fastest way to ruin a hero's load.
 */

class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.index = 0;

    if (this.slides.length < 2) {
      this.classList.add('is-single');
      return;
    }

    // Later slides' images wait for the page to finish loading, so they never
    // compete with the first slide's. Queried at promotion time rather than
    // now, so loop clones made below are covered too.
    if (document.readyState === 'complete') this.loadDeferredImages();
    else window.addEventListener('load', () => this.loadDeferredImages(), { once: true });

    this.track = this.querySelector('[data-hero-track]');
    this.dots = this.querySelector('[data-hero-dots]');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.dataset.effect === 'slide' && !this.reduced) this.classList.add('is-sliding');

    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    this.buildDots();
    this.setupLoop();
    this.bindDrag();

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1, true);
      if (event.key === 'ArrowRight') this.go(this.index + 1, true);
    });

    // Reduced motion means no timer at all, not a slower one: an auto-advancing
    // carousel is exactly the motion the setting asks us to stop.
    const interval = Number(this.dataset.autoplay);
    if (interval && !this.reduced) {
      this.interval = interval * 1000;
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      // A carousel running in a background tab burns battery to show nobody
      // anything, and lands the visitor on an arbitrary slide when they return.
      document.addEventListener('visibilitychange', () =>
        document.hidden ? this.stop() : this.start()
      );
      // Not until the page has loaded, and then a full interval later (the
      // interval's first tick). A slide change while the page is still loading
      // paints a new, larger image, and the browser then measures that one as
      // the Largest Contentful Paint — the report's 6.0s LCP was slide 3.
      if (document.readyState === 'complete') this.allowAutoplay();
      else window.addEventListener('load', () => this.allowAutoplay(), { once: true });
    }

    this.go(0, false);
  }

  disconnectedCallback() {
    this.stop();
  }

  buildDots() {
    if (!this.dots) return;
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'hero-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `${i + 1}`);
      dot.addEventListener('click', () => this.go(i, true));
      this.dots.append(dot);
    });
  }

  allowAutoplay() {
    this.autoplayAllowed = true;
    this.start();
  }

  start() {
    if (!this.autoplayAllowed || !this.interval || this.timer || document.hidden) return;
    this.timer = setInterval(() => this.go(this.index + 1, false), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Drag to change slide, the same gesture the product gallery offers.
   *
   * It commits on release rather than following the cursor. The gallery's
   * carousel mode can track a finger because its position *is* a scroll
   * offset; a hero slide is placed by transform or cross-fade, so there is no
   * continuous position to drive — and its fade mode waits for the same
   * reason. Threshold is a fraction of the width so the gesture asks the same
   * effort of a thumb on a phone as of a hand on a desktop.
   */
  bindDrag() {
    if (this.querySelectorAll('[data-hero-slide]').length < 2) return;

    let drag = null;
    let suppressClick = false;

    this.addEventListener('pointerdown', (event) => {
      // Let a press on a control be a click. Only the plate drags — and the
      // whole-banner link *is* the plate, since it covers the slide. Excluding
      // it with the other links is what stopped swipes working at all.
      if (event.button !== 0) return;
      suppressClick = false;
      const control = event.target.closest('a, button, [data-hero-prev], [data-hero-next]');
      if (control && !control.matches('.hero-slide-link')) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, moved: false };
    });

    // A swipe that began on the banner link ends in a click on it, which would
    // navigate away mid-swipe. Capture phase, so it is cancelled before the
    // link sees it. A tap never sets the flag, so it follows the link as usual.
    this.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    this.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;

      // The last position seen while moving, not the one reported at the end.
      // `pointerup` and especially `pointercancel` can arrive with a stale or
      // zeroed clientX; reading it directly made every swipe compute a large
      // negative dx and advance, whichever way the finger actually went.
      drag.lastX = event.clientX;

      if (drag.moved) return;

      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      // A few pixels of travel is a shaky press, not a swipe.
      if (Math.abs(dx) < 5) return;
      // A gesture that is mostly vertical belongs to the page, not the hero —
      // otherwise scrolling past the hero flicks it sideways.
      if (Math.abs(dy) > Math.abs(dx)) {
        drag = null;
        return;
      }

      drag.moved = true;
      // Set at the moment it becomes a drag, not only when it crosses the
      // swipe threshold: a short sideways drag that snaps back is still not
      // a tap, and should not open the link either.
      suppressClick = true;
      this.classList.add('is-dragging');
      // Capture so a drag leaving the frame still finishes here.
      this.setPointerCapture(event.pointerId);
    });

    const release = (event) => {
      if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);
      this.classList.remove('is-dragging');
    };

    this.addEventListener('pointerup', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const { moved, x, lastX } = drag;
      drag = null;
      release(event);
      if (!moved) return;

      const dx = lastX - x;
      const threshold = Math.max(40, this.clientWidth * 0.12);
      if (Math.abs(dx) < threshold) return;
      // Dragging left pulls the next slide in; dragging right pulls the
      // previous one back.
      this.go(this.index + (dx < 0 ? 1 : -1), true);
    });

    // A cancelled pointer is the browser taking the gesture over — a scroll,
    // or a system gesture. It is an abort, not a swipe: navigating on it is
    // what made the hero move on gestures the visitor never finished.
    this.addEventListener('pointercancel', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      drag = null;
      release(event);
    });

    this.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /**
   * Endless in both directions.
   *
   * Fade mode already wraps invisibly — there is no travel to see. Slide mode
   * moves a strip by `translateX`, so going from the last slide back to the
   * first rewound across every slide in between, which is the "comes back to
   * slide 1" the client saw.
   *
   * The fix is a clone of the last slide before the first and a clone of the
   * first after the last. Stepping off either end lands on a clone, which is
   * a viewport away in the direction the visitor expects; once that animation
   * finishes the track jumps to the matching real slide with the transition
   * switched off, which is invisible because the two are identical.
   *
   * Cloning rather than reordering the DOM is deliberate: the real slides keep
   * their positions, so `this.slides`, the dots, `inert` and the video
   * hydration all carry on indexing exactly as they did.
   */
  setupLoop() {
    if (!this.classList.contains('is-sliding') || !this.track) return;
    if (this.slides.length < 2) return;

    const head = this.slides[0].cloneNode(true);
    const tail = this.slides[this.slides.length - 1].cloneNode(true);

    for (const clone of [head, tail]) {
      // Not a slide as far as anything else is concerned: no data hook, out of
      // the accessibility tree, out of the tab order.
      clone.removeAttribute('data-hero-slide');
      clone.classList.remove('is-active');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      // A cloned <video> is never hydrated — `go` only walks the real slides —
      // so it would sit black. Its poster is the frame the original shows
      // before playing, which is exactly what the clone needs.
      clone.querySelectorAll('[data-hero-video]').forEach((video) => {
        const poster = video.getAttribute('poster');
        if (!poster) {
          video.remove();
          return;
        }
        const still = document.createElement('img');
        still.src = poster;
        still.alt = '';
        still.className = video.className;
        video.replaceWith(still);
      });
    }

    this.track.prepend(tail);
    this.track.append(head);
    this.loop = true;
  }

  /** Track position for a slide, in percent. Shifted by one while the loop's
   *  leading clone occupies the first place on the strip. */
  offsetFor(i) {
    return (this.loop ? i + 1 : i) * 100;
  }

  /** Animates onto a clone, then swaps to the real slide once it has landed. */
  rideClone(cloneOffset, realIndex) {
    const settle = (event) => {
      if (event.target !== this.track || event.propertyName !== 'transform') return;
      this.track.removeEventListener('transitionend', settle);
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(-${this.offsetFor(realIndex)}%)`;
      // Read back a layout value so the browser commits the jump before the
      // transition is restored; without it the two changes coalesce and the
      // jump animates, which is the rewind this exists to avoid.
      void this.track.offsetWidth;
      this.track.style.transition = '';
    };

    this.track.addEventListener('transitionend', settle);
    this.track.style.transform = `translateX(-${cloneOffset * 100}%)`;
  }

  /** `manual` restarts the timer, so a click gives a full dwell rather than
   *  whatever was left of the previous one. */
  go(next, manual) {
    // A change before `load` must not reveal a slide with no image yet. Not
    // for slide 0: connectedCallback calls go(0), and promoting everything
    // there would request every slide's image with the page after all.
    if (next !== 0) this.loadDeferredImages();
    const total = this.slides.length;
    const wrapped = ((next % total) + total) % total;

    // Off either end, and looping: travel onto the clone rather than letting
    // the modulo snap the track back across the whole strip.
    if (this.loop && this.track && (next >= total || next < 0)) {
      this.rideClone(next >= total ? total + 1 : 0, wrapped);
      this.index = wrapped;
      this.paint(manual, false);
      return;
    }

    this.index = wrapped;
    this.paint(manual, true);
  }

  /**
   * Everything a slide change means apart from moving the track: active
   * classes, focus and assistive-tech state, video playback, dots and the
   * autoplay timer.
   *
   * Split out of `go` so the looping path can do all of it while `rideClone`
   * owns the transform. `moveTrack` is false there — the clone is already
   * being animated to, and writing the real slide's offset over the top would
   * cancel that animation and produce the very jump the loop avoids.
   */
  paint(manual, moveTrack) {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      // Hidden from assistive tech and from tab order — an off-screen slide's
      // links are still focusable otherwise, and focus vanishes off-canvas.
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));

      const video = slide.querySelector('[data-hero-video]');
      if (!video) return;
      if (active) {
        HeroCarousel.hydrate(video);
        video.play?.().catch(() => {});
      } else {
        video.pause?.();
      }
    });

    if (moveTrack && this.classList.contains('is-sliding') && this.track) {
      this.track.style.transform = `translateX(-${this.offsetFor(this.index)}%)`;
    }

    this.dots?.querySelectorAll('.hero-dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-selected', String(i === this.index));
    });

    if (manual && this.interval) {
      this.stop();
      this.start();
    }
  }

  /** Promotes every deferred slide image, and its <picture> sources, once. */
  loadDeferredImages() {
    for (const img of this.querySelectorAll('img[data-hero-defer]')) {
      for (const source of img.parentElement.querySelectorAll('source[data-srcset]')) {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      }
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
      img.removeAttribute('data-hero-defer');
    }
  }

  /** Promotes `data-src` to `src` once, then tells the element to fetch. */
  static hydrate(video) {
    if (video.dataset.ready) return;
    video.dataset.ready = 'true';

    const pending = video.querySelectorAll('source[data-src]');
    if (!pending.length) return;

    for (const source of pending) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    }
    video.load();
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
